import {
  AfterViewInit,
  Component,
  DestroyRef,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Store } from "@ngxs/store";
import { LayoutBreakPointsService } from "@shared/services/layout-break-points.service";
import { Chart } from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment";

@Component({
  selector: "app-line-chart",
  templateUrl: "./line-chart.component.html",
  styleUrls: ["./line-chart.component.scss"],
  standalone: true,
  imports: [],
})
export class LineChartComponent implements OnInit, AfterViewInit, OnChanges {
  private readonly destroyRef = inject(DestroyRef);

  @Input() public chartId: any;
  @Input() public desktopScreen: any;
  @Input() public agentSelected: any;

  private axisData: any;
  private axisLabels: any;
  private lineChart: any;
  private readonly myLineChart: any[] = [];
  private lineChartData: any;
  private desktopView: boolean = false;
  protected showChart: any;
  private maxValue: any;
  private minValue: any;
  private lineValue: any;
  private readonly portfolioGwp: any;
  private axisDatas: any;

  constructor(
    private readonly layoutBreakpointService: LayoutBreakPointsService,
    private readonly store: Store,
  ) {}

  ngOnInit(): void {
    this.layoutBreakpointService
      .subscribeLayoutChanges()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.desktopView = this.layoutBreakpointService.renderDesktop();
      });

    this.getLineChart();
  }

  ngAfterViewInit(): void {
    if (this.chartId) {
      this.createLineChart();
    }
    this.getLineChart();
  }

  ngOnChanges(): void {
    if (this.agentSelected) {
      if (this.lineChart !== undefined) {
        this.lineChart.destroy();
        this.getLineChart();
        if (this.chartId) {
          this.createLineChart();
        }
      }
    }
  }

  @HostListener("window:orientationchange", ["$event"])
  protected onOrientationChange(_event: Event): void {
    this.lineChart.destroy();
    this.createLineChart();
  }

  private getLineChart(): void {
    const lineData = [];
    const lineValues = [];
    const lineLabels = [];
    this.lineChartData = this.store.selectSnapshot<any>(
      (state) => state.UserPerformanceState.gwpData.monthGrowth,
    );

    this.lineChartData.forEach((item) => {
      lineValues.push(item.value);
      lineData.push(item.percentage);
      lineLabels.push(item.month);
      this.lineValue = lineValues;
      this.axisData = lineData;
      this.axisLabels = lineLabels;

      this.axisDatas = this.axisData.filter((n) => {
        return n;
      });
      if (this.axisDatas.length > 0) {
        this.showChart = true;
        document.getElementById("chartLine").hidden = false;
      } else {
        this.showChart = false;
        document.getElementById("chartLine").hidden = true;
      }
    });
    this.maxValue = Math.max.apply(null, this.axisData);
    this.minValue = Math.min.apply(null, this.axisData);
    this.axisLabels = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
  }

  private createLineChart(): void {
    const ctx = document.getElementById(this.chartId) as HTMLCanvasElement;
    const dataValue = this.lineValue;
    const c = ctx.getContext("2d");
    const radialGradient = c.createLinearGradient(0, 0, 10, 10);
    radialGradient.addColorStop(0.8, "#ffffff");
    radialGradient.addColorStop(1, "red");

    const yScaleLabel = {
      id: "yScaleLabel",
      afterDatasetsDraw(chart, _args, _options) {
        const {
          ctx,
          chartArea: { top },
        } = chart;
        ctx.save();
        ctx.font = "bold 13px Helvetica Neue , Arial, sans-serif";
        ctx.fillText("%", 36, top - 28);
        ctx.restore();
      },
    };
    this.lineChart = new Chart(ctx, {
      plugins: [ChartDataLabels, yScaleLabel],
      type: "line",
      data: {
        labels: this.axisLabels,
        datasets: [
          {
            data: this.axisData,
            borderWidth: 2,
            borderColor: "#007AB3",
            backgroundColor: ["rgba(222, 222, 222, 0.5)"],
            pointBackgroundColor: "rgb(255, 255, 255)",
            pointHoverBackgroundColor: "rgb(0, 97, 146)",
            pointHoverBorderWidth: 3,
            pointHoverRadius: 4,
            pointRadius: this.desktopView ? 3 : 5.5,
            pointHitRadius: this.desktopView ? 6 : 5.5,
          },
        ],
      },

      options: {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
          datalabels: {
            color: (context) => {
              const index = context.dataIndex;
              const value = context.dataset.data[index];
              return (value as number) < 0 ? "#dc3149" : "#1e8927";
            },
            align: "top",
            font: {
              size: this.desktopView ? 14 : 12,
            },
            formatter: (value) => {
              const percentage = value.toFixed(1) + "%";
              return percentage;
            },
          },
          legend: {
            display: false,
            position: "bottom",
            align: "start",
            labels: {
              boxWidth: 80,
              font: () => {
                return {
                  size: 24,
                };
              },
            },
          },
          tooltip: {
            enabled: false,
            position: "nearest",
            external: (context) => {
              let tooltipEl = document.getElementById("chartjs-tooltip");
              if (!tooltipEl) {
                tooltipEl = document.createElement("div");
                tooltipEl.id = "chartjs-tooltip";
                tooltipEl.innerHTML = "<table></table>";
                document.body.appendChild(tooltipEl);
              }

              const tooltipModel = context.tooltip;
              if (tooltipModel.opacity === 0) {
                tooltipEl.style.opacity = " 0";
                return;
              }

              tooltipEl.classList.remove("above", "below", "no-transform");
              if (tooltipModel.yAlign) {
                tooltipEl.classList.add(tooltipModel.yAlign);
                tooltipEl.classList.add("linechart-tooltip");
              } else {
                tooltipEl.classList.add("no-transform");
              }

              function getBody(bodyItem) {
                const arr = [];
                const bodytitle = tooltipModel.title;
                const keys = bodytitle;
                const values = bodyItem.lines;
                for (let i = 0; i < keys.length; i++) {
                  arr[i] = keys + ":" + values[i];
                }
                return arr;
              }

              if (tooltipModel.body) {
                const titleLines = tooltipModel.title || [];
                const bodyLines = tooltipModel.body.map(getBody);

                let innerHtml = "<thead>";

                titleLines.forEach((_title) => {
                  innerHtml += "";
                });
                innerHtml += '</thead><tbody style="padding-top:120px">';

                bodyLines.forEach((body) => {
                  const bodyString = body[0].toString();
                  let title = bodyString.split(":")[0];
                  const bodyText = +bodyString.split(":")[1];
                  const bodyTexts = Number.isInteger(bodyText)
                    ? bodyText.toFixed(1)
                    : bodyText;
                  title = moment(title, "MMM").format("MMMM");
                  const titleDiv =
                    '<span class="line-tooltip-text">' +
                    title +
                    "</span></div>";
                  let valuePoint = new Intl.NumberFormat("en-MY").format(
                    Math.round(dataValue[tooltipModel.dataPoints[0].dataIndex]),
                  );
                  if (parseInt(valuePoint) > 0) {
                    valuePoint = `<span style="color:green;display: flex; justify-content: center; "> RM ${valuePoint
                      .toString()
                      .slice(
                        0,
                        valuePoint.toString().length,
                      )} (${bodyTexts}%)</span>`;
                  } else {
                    valuePoint = `<span style="color:red;display: flex; justify-content: center;">${valuePoint
                      .toString()
                      .slice(
                        0,
                        1,
                      )} RM ${valuePoint.toString().slice(1, valuePoint.toString().length)} (${bodyTexts}%)`;
                  }
                  const span =
                    "<div class='tooltips-amount'>" + valuePoint + "</div>";
                  innerHtml +=
                    titleDiv + span + '<div class="line-caret"></div>';
                });
                innerHtml += "</tbody>";

                const tableRoot = tooltipEl.querySelector("table");
                tableRoot.innerHTML = innerHtml;
              }

              const position = context.chart.canvas.getBoundingClientRect();

              tooltipEl.style.opacity = " 1";
              tooltipEl.style.position = "absolute";

              tooltipEl.style.left =
                position.left +
                window.pageXOffset +
                (tooltipModel.caretX - 83) +
                "px";
              tooltipEl.style.top =
                position.top +
                window.pageYOffset +
                (tooltipModel.caretY - 99) +
                "px";
              tooltipEl.style.padding = "20px 22px 0px 30px";
              tooltipEl.style.margin = "auto";
              tooltipEl.style.boxShadow = "2px 2px 4px 2px #41414180";
              tooltipEl.style.width = "165px";
              tooltipEl.style.height = "90px";
              tooltipEl.style.pointerEvents = "none";
              tooltipEl.style.backgroundColor = "#FFF";
              tooltipEl.style.borderRadius = "3px";
              tooltipEl.style.zIndex = "10";
              tooltipEl.style.fontSize = "13px";
              tooltipEl.style.whiteSpace = "nowrap";
            },
          },
        },
        animation: {
          duration: 2000,
        },

        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 50,
            bottom: 0,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            display: true,
            stacked: true,
            grid: {
              drawOnChartArea: false,
              drawTicks: false,
            },
            suggestedMin: -1,
            suggestedMax: 1,
            ticks: {
              color: () => {
                return "#767676";
              },
              font: () => {
                return {
                  size: 12,
                  family: "Allianz Neo , Arial, sans-serif",
                  padding: 12,
                };
              },
              stepSize: 20,
              padding: 12,
            },
          },

          x: {
            offset: true,
            grid: {
              drawOnChartArea: false,
              drawTicks: false,
            },
            suggestedMin: -1,
            suggestedMax: 1,
            ticks: {
              color: () => {
                return "#767676";
              },
              font: () => {
                return {
                  size: 14,
                  family: "Allianz Neo , Arial, sans-serif",
                  lineHeight: 2.2,
                };
              },
              padding: 2,
            },
          },
        },
        elements: {
          line: {
            fill: false,
            tension: 0,
          },
        },
      },
    });
  }

  protected getDesktopView(): boolean {
    return this.desktopView;
  }

  private getDecmials(_num: any): void {
    // return this._decimalPipe.transform(num, '1.1-2')
  }
}
